import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import TextEditorButton from 'flarum/common/components/TextEditorButton';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import classList from 'flarum/common/utils/classList';

// 语言选择模态框
class LanguageSelectModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    
    this.selectedLanguage = this.attrs.selectedLanguage || '';
    this.languageOptions = this.attrs.languageOptions || {};
  }

  className() {
    return 'LanguageSelectModal Modal--small';
  }

  title() {
    return app.translator.trans('tf-translate.forum.select_language_modal_title');
  }

  view() {
    const options = [
      // { value: '', text: app.translator.trans('tf-translate.forum.please_select_language') },
      ...Object.keys(this.languageOptions || {}).map(lang => ({
        value: lang,
        text: this.languageOptions[lang]
      }))
    ];
    
    return (
      <div className={`Modal modal-dialog ${this.className()}`}>
        <div className="Modal-content">
          <div className="Modal-header">
            <h3 className="App-titleControl App-titleControl--text">{this.title()}</h3>
          </div>
          
          <div className="Modal-body">
            <div className="Form-group">
              <label className="Form-label">{app.translator.trans('tf-translate.forum.select_target_language_label')}</label>
              <div className="LanguageSelectModal-options">
                {options.map(option => (
                  <label className="LanguageSelectModal-option" key={option.value}>
                    <input 
                      type="radio" 
                      name="language" 
                      value={option.value}
                      checked={this.selectedLanguage === option.value}
                      onchange={(e) => {
                        this.selectedLanguage = e.target.value;
                        m.redraw();
                      }}
                    />
                    <span className="LanguageSelectModal-option-text">{option.text}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          <div className="Modal-footer">
            <Button
              className="Button"
              onclick={this.hide.bind(this)}
            >
              {app.translator.trans('tf-translate.forum.cancel')}
            </Button>
            <Button
              className="Button Button--primary"
              onclick={this.confirmAndTranslate.bind(this)}
              disabled={!this.selectedLanguage}
            >
              {app.translator.trans('tf-translate.forum.confirm_and_translate')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  confirmAndTranslate() {
    if (this.selectedLanguage && this.attrs.onConfirm) {
      // 保存选择的语言
      localStorage.setItem('tf-translate-target-language', this.selectedLanguage);
      
      // 执行翻译回调
      this.attrs.onConfirm(this.selectedLanguage);
      this.hide();
    }
  }
}

export default class TranslateButton extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    
    this.translateLoading = false;
    
    // 从本地存储读取设置
    const savedSettings = localStorage.getItem('tf-translate-settings');
    const settings = savedSettings ? JSON.parse(savedSettings) : { enabled: '1' };
    this.isEnabled = settings.enabled === '1';
    
    if (!this.isEnabled) {
      return;
    }
    
    // 语言选项键 - 根据API接口要求
    // 注意：显示名称会在 view() 方法中通过翻译获取
    this.languageKeys = {
      '中文': 'chinese',
      '英文': 'english', 
      '日文': 'japanese',
      '韩文': 'korean',
      '法文': 'french'
    };
    
    // 语言选项映射（用于显示）
    this.getLanguageOptions = () => {
      return {
        '中文': app.translator.trans('tf-translate.forum.languages.chinese'),
        '英文': app.translator.trans('tf-translate.forum.languages.english'), 
        '日文': app.translator.trans('tf-translate.forum.languages.japanese'),
        '韩文': app.translator.trans('tf-translate.forum.languages.korean'),
        '法文': app.translator.trans('tf-translate.forum.languages.french')
      };
    };
    
    // 从本地存储获取用户选择的语言
    this.selectedLanguage = localStorage.getItem('tf-translate-target-language') || '';
  }

  translateButtonClicked(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // 显示语言选择模态框，确认后自动翻译
    app.modal.show(LanguageSelectModal, {
      selectedLanguage: this.selectedLanguage,
      languageOptions: this.getLanguageOptions(),
      onConfirm: (language) => {
        this.selectedLanguage = language;
        m.redraw();
        
        // 自动执行翻译
        this.translateContent();
      }
    });
  }

  view() {
    if (!this.isEnabled) {
      return null;
    }

    // 检查用户是否有翻译权限
    const user = app.session.user;
    if (!user) {
      return null;
    }

    // 从用户属性中获取 canTranslate 权限
    // 如果后端返回了 canTranslate 属性，可以直接使用
    const canTranslate = user.attribute('canTranslate');
    if (!canTranslate) {
      return null;
    }

    // 动态获取当前选择的语言
    const currentLanguage = localStorage.getItem('tf-translate-target-language') || '';
    const languageOptions = this.getLanguageOptions();
    const languageDisplay = currentLanguage ? ` (${languageOptions[currentLanguage] || currentLanguage})` : '';
    
    const translateText = app.translator.trans('tf-translate.forum.translate');
    const translatingText = app.translator.trans('tf-translate.forum.translating');
    const buttonText = this.translateLoading
      ? translatingText
      : `${translateText}${languageDisplay}`;

    let titleText;
    if (this.translateLoading) {
      titleText = translatingText;
    } else if (currentLanguage) {
      titleText = `${app.translator.trans('tf-translate.forum.translate_to')}${languageDisplay}`;
    } else {
      titleText = app.translator.trans('tf-translate.forum.click_to_select_and_translate');
    }

    return (
        <TextEditorButton
          className={classList([
            'Button',
            'hasIcon',
            'tf-translate-button',
            !this.translateLoading && 'Button--icon',
            !this.translateLoading && 'Button--link',
            this.translateLoading && 'loading',
          ])}
          icon={!this.translateLoading && 'fa-whiteboard fa-semibold fa-language'}
          onclick={this.translateButtonClicked.bind(this)}
          disabled={this.translateLoading}
          title={titleText}
        >
          {this.translateLoading && <LoadingIndicator size="small" display="inline" className="Button-icon" />}
          {this.translateLoading && <span className="Button-label">{buttonText}</span>}
          {!this.translateLoading && currentLanguage && <span className="Button-label">{buttonText}</span>}
        </TextEditorButton>
    );
  }

  translateContent() {
    const editor = this.attrs.editor;
    const content = editor.value;
    
    if (!content || !content.trim()) {
      app.alerts.show({ type: 'warning' }, app.translator.trans('tf-translate.forum.please_input_content'));
      return;
    }

    // 使用当前选择的语言（模态框确认时已更新）
    const currentLanguage = this.selectedLanguage || localStorage.getItem('tf-translate-target-language') || '';
    if (!currentLanguage) {
      app.alerts.show({ type: 'warning' }, app.translator.trans('tf-translate.forum.please_select_target_language'));
      return;
    }

    this.translateLoading = true;
    m.redraw();

    // 调用真实的翻译API
    this.callTranslateAPI(content, currentLanguage)
      .then(translatedText => {
        // 尝试多种方式更新编辑器内容
        try {
          // 方法1: 直接设置value
          editor.value = translatedText;
          
          // 方法2: 尝试调用oninput回调
          if (editor.attrs && editor.attrs.oninput) {
            editor.attrs.oninput(translatedText);
          }
          
          // 方法3: 直接操作DOM元素
          const editorElement = editor.$('.TextEditor-editor')[0];
          if (editorElement) {
            editorElement.value = translatedText;
            editorElement.dispatchEvent(new Event('input', { bubbles: true }));
            editorElement.dispatchEvent(new Event('change', { bubbles: true }));
          }
          
          // 方法4: 尝试触发redraw
          m.redraw();
        } catch (error) {
          console.error('更新编辑器内容失败:', error);
        }
        
        app.alerts.show({ type: 'success' }, app.translator.trans('tf-translate.forum.translate_success'));
        this.translateLoading = false;
        m.redraw();
      })
      .catch(error => {
        console.error('翻译失败:', error);
        app.alerts.show({ type: 'error' }, app.translator.trans('tf-translate.forum.translation_failed') + ': ' + error.message);
        this.translateLoading = false;
        m.redraw();
      });
  }

  async callTranslateAPI(content, language) {
    try {
      const response = await fetch(app.forum.attribute('apiUrl')+'/translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': app.session.csrfToken
        },
        body: JSON.stringify({
          content: content,
          language: language
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      return data.translated_content || data.translated_text || data.result || data.translation || app.translator.trans('tf-translate.forum.translation_failed');
    } catch (error) {
      console.error('翻译API调用失败:', error);
      throw error;
    }
  }

  mockTranslate(text, targetLanguage) {
    const mockTranslations = {
      'en': {
        '你好': 'Hello',
        '世界': 'World',
        '翻译': 'Translate',
        '测试': 'Test',
        '这是一个测试': 'This is a test',
        '欢迎': 'Welcome',
        '谢谢': 'Thank you',
        '再见': 'Goodbye'
      },
      'zh': {
        'Hello': '你好',
        'World': '世界',
        'Translate': '翻译',
        'Test': '测试',
        'This is a test': '这是一个测试',
        'Welcome': '欢迎',
        'Thank you': '谢谢',
        'Goodbye': '再见'
      },
      'ja': {
        'Hello': 'こんにちは',
        'World': '世界',
        'Translate': '翻訳',
        'Test': 'テスト',
        'This is a test': 'これはテストです',
        'Welcome': 'ようこそ',
        'Thank you': 'ありがとう',
        'Goodbye': 'さようなら'
      },
      'ko': {
        'Hello': '안녕하세요',
        'World': '세계',
        'Translate': '번역',
        'Test': '테스트',
        'This is a test': '이것은 테스트입니다',
        'Welcome': '환영합니다',
        'Thank you': '감사합니다',
        'Goodbye': '안녕히 가세요'
      }
    };

    let translatedText = text;
    const translations = mockTranslations[targetLanguage] || {};
    
    // 简单的词汇替换
    Object.keys(translations).forEach(original => {
      const regex = new RegExp(original, 'gi');
      translatedText = translatedText.replace(regex, translations[original]);
    });

    // 如果没有任何翻译，添加一个标记
    if (translatedText === text) {
      translatedText = `[${targetLanguage.toUpperCase()}] ${text}`;
    }

    return translatedText;
  }
}
